import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import * as Forge from 'node-forge';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EncrypDepcrypService {
    private aesKey: CryptoJS.lib.WordArray | null = null;
    private aesIv: CryptoJS.lib.WordArray | null = null;
    private sessionId: string | null = null;
    private rsaPublicKey: string = '';
    private initPromise: Promise<void> | null = null;

    constructor(private http: HttpClient) {}

    initialize(): Promise<void> {
        if (!environment.isCloud) return Promise.resolve();
        if (this.initPromise) return this.initPromise;

        // Always create new keys on reload
        this.initPromise = this.performInitialization()
            .then(() => {})
            .finally(() => {
                this.initPromise = null;
            });

        return this.initPromise;
    }

    private performInitialization(): Promise<void> {
        return this.attempt(() => this.initializeSession()).then((result) => {
            if ('error' in result) {
                throw new Error(
                    `Session initialization failed: ${result.error}`
                );
            }
        });
    }

    private initializeSession(): Promise<void> {
        this.clearSession();

        return this.http
            .get(`${environment.apiUrl}Auth/PublicKey`, {
                responseType: 'text',
            })
            .toPromise()
            .then((rsaPublicKey) => {
                this.rsaPublicKey = rsaPublicKey!;
                this.aesKey = CryptoJS.lib.WordArray.random(32);
                this.aesIv = CryptoJS.lib.WordArray.random(16);
                this.sessionId = this.generateSessionId();
                return this.registerSessionKeys();
            });
    }

    public isKeyPresent(): boolean {
        // Returns true if both key and IV are present
        return !!(this.aesKey && this.aesIv);
    }

    private registerSessionKeys(): Promise<void> {
        const keyData = JSON.stringify({
            SessionId: this.sessionId,
            Key: this.aesKey!.toString(CryptoJS.enc.Base64),
            IV: this.aesIv!.toString(CryptoJS.enc.Base64),
        });

        const rsa = Forge.pki.publicKeyFromPem(this.rsaPublicKey);
        const encryptedKeyData = window.btoa(rsa.encrypt(keyData));

        return this.http
            .post(`${environment.apiUrl}Auth/register-key`, encryptedKeyData, {
                headers: new HttpHeaders().set('Content-Type', 'text/plain'),
                responseType: 'text',
            })
            .toPromise()
            .then(() => {});
    }

    private attempt<T>(
        operation: () => Promise<T>
    ): Promise<{ data: T } | { error: string }> {
        return operation()
            .then((data) => ({ data }))
            .catch((error) => ({
                error: error instanceof Error ? error.message : String(error),
            }));
    }

    private clearSession(): void {
        this.aesKey = null;
        this.aesIv = null;
        this.sessionId = null;
    }

    private generateSessionId(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    encrypt(dataToEncrypt: string): string {
        if (!this.aesKey || !this.aesIv) {
            throw new Error('Encryption service not initialized');
        }

        const encryptedData = CryptoJS.AES.encrypt(
            CryptoJS.enc.Utf8.parse(dataToEncrypt),
            this.aesKey,
            {
                keySize: 32,
                iv: this.aesIv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            }
        );

        return encryptedData
            .toString()
            .replaceAll(';', '[1111111]')
            .replaceAll('/', '[2222222]')
            .replaceAll('?', '[3333333]')
            .replaceAll(':', '[4444444]')
            .replaceAll('@', '[5555555]')
            .replaceAll('&', '[6666666]')
            .replaceAll('=', '[7777777]')
            .replaceAll('+', '[8888888]')
            .replaceAll('$', '[9999999]')
            .replaceAll(',', '[1211111]');
    }

    decrypt(encryptedValue: string): string {
        if (!encryptedValue || !this.aesKey || !this.aesIv) {
            return encryptedValue;
        }

        const decrypted = CryptoJS.AES.decrypt(encryptedValue, this.aesKey, {
            keySize: 256 / 32,
            iv: this.aesIv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        return decrypted.toString(CryptoJS.enc.Utf8);
    }

    decryptAsJsonObject(encryptedValue: string): any {
        if (!encryptedValue) {
            return encryptedValue;
        }

        const decrypted = CryptoJS.AES.decrypt(encryptedValue, this.aesKey!, {
            keySize: 256 / 32,
            iv: this.aesIv!,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

        try {
            return JSON.parse(decryptedString);
        } catch (e) {
            console.error('Error parsing decrypted data:', e);
            return null;
        }
    }

    encryptBody(body: any): string {
        if (!body) {
            return '';
        }

        const stringifiedBody = JSON.stringify(body);
        return this.encrypt(stringifiedBody);
    }

    getSessionId(): string {
        if (!this.sessionId) {
            throw new Error('Session not initialized');
        }
        return this.sessionId;
    }
}
